import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Store,
  UserCheck,
  Bike,
  PackageSearch,
  PackageOpen,
  Warehouse,
  Truck,
  Package as PackageIcon,
  PackageCheck,
  Activity,
} from "lucide-react";

const CARDS = [
  { key: "store_owners_total", label: "Registered Store Owners", icon: Store, accent: "text-cyan-400" },
  { key: "store_owners_pending", label: "Store Owners Pending", icon: UserCheck, accent: "text-yellow-400" },
  { key: "riders_total", label: "Registered Riders", icon: Bike, accent: "text-cyan-400" },
  { key: "riders_pending", label: "Riders Pending Approval", icon: UserCheck, accent: "text-yellow-400" },
  { key: "packages_waiting", label: "Packages Waiting for Pickup", icon: PackageSearch, accent: "text-yellow-400" },
  { key: "packages_picked_up", label: "Picked Up by Rider", icon: PackageOpen, accent: "text-blue-400" },
  { key: "packages_received", label: "Received at Warehouse", icon: Warehouse, accent: "text-purple-400" },
  { key: "packages_shipped", label: "Shipped Out", icon: Truck, accent: "text-orange-400" },
  { key: "packages_last_mile", label: "Out for Last-Mile", icon: PackageIcon, accent: "text-teal-400" },
  { key: "packages_delivered", label: "Delivered to Customer", icon: PackageCheck, accent: "text-green-400" },
];

export default function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  const totalPackages = stats
    ? CARDS.slice(4).reduce((acc, c) => acc + (stats[c.key] || 0), 0)
    : 0;

  return (
    <div data-testid="overview-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
            / Dashboard Overview
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Command Center
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Real-time pulse of every package, store, and rider on the network.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono-tech text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 bg-slate-100/40 dark:bg-slate-900/40">
          <Activity className="w-4 h-4 text-cyan-400" />
          <span>{totalPackages} packages in network</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map((c) => (
          <div
            key={c.key}
            data-testid={`stat-card-${c.key}`}
            className="group bg-slate-100/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg p-5 hover:border-slate-300 hover:dark:border-slate-700 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <c.icon className={`w-5 h-5 ${c.accent}`} />
              <span className="text-[10px] font-mono-tech text-slate-500 uppercase tracking-wider">
                LIVE
              </span>
            </div>
            <div className="font-display text-3xl font-bold">
              {stats == null ? "—" : (stats[c.key] ?? 0)}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-snug">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-slate-100/40 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-lg p-6">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-4">
            / Pipeline Distribution
          </div>
          <div className="space-y-3">
            {[
              ["Waiting for Pickup", stats?.packages_waiting, "bg-yellow-400"],
              ["Picked Up by Rider", stats?.packages_picked_up, "bg-blue-400"],
              ["Received at Warehouse", stats?.packages_received, "bg-purple-400"],
              ["Shipped Out", stats?.packages_shipped, "bg-orange-400"],
              ["Out for Last-Mile", stats?.packages_last_mile, "bg-teal-400"],
              ["Delivered", stats?.packages_delivered, "bg-green-400"],
            ].map(([label, val, color]) => {
              const max = Math.max(totalPackages, 1);
              const pct = Math.round(((val || 0) / max) * 100);
              return (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700 dark:text-slate-300">{label}</span>
                    <span className="font-mono-tech text-slate-500">
                      {val ?? 0} · {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                    <div className={`${color} h-full`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-slate-100/40 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-lg p-6">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-4">
            / Quick Triage
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center justify-between p-3 rounded-md border border-yellow-500/20 bg-yellow-500/5">
              <span className="text-slate-200">Store Owners pending approval</span>
              <span className="font-display text-lg font-bold text-yellow-400">
                {stats?.store_owners_pending ?? 0}
              </span>
            </li>
            <li className="flex items-center justify-between p-3 rounded-md border border-yellow-500/20 bg-yellow-500/5">
              <span className="text-slate-200">Riders pending approval</span>
              <span className="font-display text-lg font-bold text-yellow-400">
                {stats?.riders_pending ?? 0}
              </span>
            </li>
            <li className="flex items-center justify-between p-3 rounded-md border border-cyan-500/20 bg-cyan-500/5">
              <span className="text-slate-200">Packages in motion</span>
              <span className="font-display text-lg font-bold text-cyan-400">
                {(stats?.packages_picked_up ?? 0) +
                  (stats?.packages_received ?? 0) +
                  (stats?.packages_shipped ?? 0) +
                  (stats?.packages_last_mile ?? 0)}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
